import { nanoid } from 'nanoid';
import type {
  BootState,
  ChatMessage,
  Diagram,
  DiagramRevision,
  Project,
  StudioState,
} from './types.ts';
import {
  DEFAULT_MODEL,
  LEGACY_ONBOARDING_KEY,
  LEGACY_STORE_KEY_V1,
  LEGACY_STORE_KEY_V2,
  MAX_REVISIONS,
  ONBOARDING_KEY,
  STORE_KEY,
} from './types.ts';

function nowIso(): string {
  return new Date().toISOString();
}

export function safeRead(storage: Storage, key: string): string {
  try {
    return storage.getItem(key) || '';
  } catch {
    return '';
  }
}

export function safeWrite(storage: Storage, key: string, value: string) {
  try {
    if (value.trim()) {
      storage.setItem(key, value);
    } else {
      storage.removeItem(key);
    }
  } catch {
    // ignore storage write errors
  }
}

export function makeProject(name: string): Project {
  const t = nowIso();
  return { id: nanoid(10), name, createdAt: t, updatedAt: t };
}

export function makeRevision(code: string, source: DiagramRevision['source'], note: string): DiagramRevision {
  return { id: nanoid(10), mermaidCode: code, source, note, createdAt: nowIso() };
}

export function makeDiagram(projectId: string, title: string): Diagram {
  const t = nowIso();
  const initial = makeRevision('', 'seed', 'Initial diagram');
  return {
    id: nanoid(10),
    projectId,
    title,
    type: 'mermaid',
    mermaidCode: '',
    theme: 'light',
    chat: [],
    revisions: [initial],
    revisionCursor: 0,
    createdAt: t,
    updatedAt: t,
  };
}

export function seedStore(): StudioState {
  const project = makeProject('Default Project');
  const diagram = makeDiagram(project.id, 'Untitled Diagram');
  return {
    model: DEFAULT_MODEL,
    diagramSortMode: 'recent',
    projects: [project],
    diagrams: [diagram],
    selectedProjectId: project.id,
    selectedDiagramId: diagram.id,
  };
}

function normalizeDiagram(raw: Partial<Diagram>, fallbackProjectId: string): Diagram {
  const t = nowIso();
  const mermaidCode = typeof raw.mermaidCode === 'string' ? raw.mermaidCode : '';
  const revisionsInput = Array.isArray(raw.revisions) ? raw.revisions : [];

  const revisions = revisionsInput
    .filter((item): item is DiagramRevision => typeof item?.mermaidCode === 'string')
    .map((item) => ({
      id: item.id || nanoid(10),
      mermaidCode: item.mermaidCode,
      source: item.source || 'editor' as const,
      note: item.note || 'Edit',
      createdAt: item.createdAt || t,
    }));

  if (!revisions.length) {
    revisions.push(makeRevision(mermaidCode, 'seed', 'Initial diagram'));
  }

  let revisionCursor =
    typeof raw.revisionCursor === 'number'
      ? Math.max(0, Math.min(raw.revisionCursor, revisions.length - 1))
      : revisions.length - 1;

  if (revisions[revisionCursor]?.mermaidCode !== mermaidCode) {
    revisions.push(makeRevision(mermaidCode, 'editor', 'Migrated code snapshot'));
    revisionCursor = revisions.length - 1;
  }

  const trimmed = revisions.slice(-MAX_REVISIONS);
  const cursorOffset = revisions.length - trimmed.length;

  return {
    id: raw.id || nanoid(10),
    projectId: raw.projectId || fallbackProjectId,
    title: raw.title || 'Untitled Diagram',
    type: raw.type === 'excalidraw' ? 'excalidraw' : 'mermaid',
    mermaidCode,
    excalidrawData: raw.excalidrawData,
    theme: raw.theme === 'dark' ? 'dark' : 'light',
    pinned: raw.pinned === true,
    tags: Array.isArray(raw.tags) ? raw.tags.filter((v): v is string => typeof v === 'string') : undefined,
    chat: Array.isArray(raw.chat)
      ? raw.chat
          .filter((message): message is ChatMessage => typeof message?.text === 'string')
          .map((message) => ({
            id: message.id || nanoid(8),
            role: message.role || 'assistant',
            text: message.text,
            createdAt: message.createdAt || t,
          }))
      : [],
    revisions: trimmed,
    revisionCursor: Math.max(0, revisionCursor - cursorOffset),
    createdAt: raw.createdAt || t,
    updatedAt: raw.updatedAt || t,
  };
}

export function persistStore(store: StudioState) {
  safeWrite(localStorage, STORE_KEY, JSON.stringify(store));
}

export function readBootState(): BootState {
  const fallback = seedStore();

  try {
    // Try new key first, then legacy v2, then legacy v1
    let raw = localStorage.getItem(STORE_KEY);
    let parsed = raw ? (JSON.parse(raw) as Partial<StudioState> | null) : null;

    if (!parsed) {
      raw = localStorage.getItem(LEGACY_STORE_KEY_V2);
      parsed = raw ? (JSON.parse(raw) as Partial<StudioState> | null) : null;
    }

    if (!parsed) {
      const legacy = localStorage.getItem(LEGACY_STORE_KEY_V1);
      parsed = legacy ? (JSON.parse(legacy) as Partial<StudioState>) : null;
    }

    if (!parsed?.projects?.length) {
      return {
        store: fallback,
        onboardingDone:
          safeRead(localStorage, ONBOARDING_KEY) === '1' ||
          safeRead(localStorage, LEGACY_ONBOARDING_KEY) === '1',
      };
    }

    const projects = parsed.projects
      .filter((project): project is Project => Boolean(project?.id))
      .map((project) => ({
        id: project.id,
        name: project.name || 'Untitled Project',
        createdAt: project.createdAt || nowIso(),
        updatedAt: project.updatedAt || nowIso(),
      }));

    if (!projects.length) {
      return {
        store: fallback,
        onboardingDone:
          safeRead(localStorage, ONBOARDING_KEY) === '1' ||
          safeRead(localStorage, LEGACY_ONBOARDING_KEY) === '1',
      };
    }

    const projectIds = new Set(projects.map((project) => project.id));

    const normalizedDiagrams = (Array.isArray(parsed.diagrams) ? parsed.diagrams : [])
      .map((diagram) => normalizeDiagram(diagram, projects[0].id))
      .filter((diagram) => projectIds.has(diagram.projectId));

    const diagrams = normalizedDiagrams.length
      ? normalizedDiagrams
      : [makeDiagram(projects[0].id, 'Untitled Diagram')];

    const selectedProjectId =
      projects.find((project) => project.id === parsed.selectedProjectId)?.id ||
      diagrams.find((diagram) => diagram.projectId === parsed.selectedProjectId)?.projectId ||
      projects[0].id;

    const projectDiagrams = diagrams.filter((diagram) => diagram.projectId === selectedProjectId);

    const selectedDiagramId =
      projectDiagrams.find((diagram) => diagram.id === parsed.selectedDiagramId)?.id ||
      projectDiagrams[0]?.id ||
      diagrams[0].id;

    const store: StudioState = {
      model: parsed.model?.trim() || DEFAULT_MODEL,
      diagramSortMode:
        parsed.diagramSortMode === 'oldest' || parsed.diagramSortMode === 'alpha'
          ? parsed.diagramSortMode
          : 'recent',
      projects,
      diagrams,
      selectedProjectId,
      selectedDiagramId,
    };

    return {
      store,
      onboardingDone:
        safeRead(localStorage, ONBOARDING_KEY) === '1' ||
        safeRead(localStorage, LEGACY_ONBOARDING_KEY) === '1',
    };
  } catch {
    return {
      store: fallback,
      onboardingDone:
        safeRead(localStorage, ONBOARDING_KEY) === '1' ||
        safeRead(localStorage, LEGACY_ONBOARDING_KEY) === '1',
    };
  }
}
